/**
 * Acaera Design System
 * ========================
 * Single source of truth for UI patterns across the app.
 * Import tokens and presets into components to stay consistent.
 *
 * Aesthetic: Editorial / Sharp — inspired by magazine layouts.
 * Font: SF Pro Display (400–900), set globally via fonts.css.
 * Color theming: CSS variables --color-accent1/accent2 driven by
 * data-color-scheme attribute (blue | green | purple | amber).
 *
 * RULES:
 *  1. Always rounded-sm (2px) — never rounded-lg or rounded-full.
 *  2. Cards are bg-white dark:bg-gray-800 with border-gray-200/700.
 *  3. Headings use font-black + tracking-tight. Body uses font-bold max.
 *  4. Labels are text-xs uppercase tracking-wider (or tracking-widest).
 *  5. Accent bars: h-1 bg-accent1 rounded-sm (w-8 on cards, w-12 on sections).
 *  6. Interactive hover: whileHover={{ y: -2 }} or border-accent1 highlight.
 *  7. Status badges: rounded-sm, never rounded-full pills.
 *  8. Empty states: border-2 border-dashed rounded-sm, centered icon + text.
 *  9. Icons: lucide-react only. Sizes h-3/h-4/h-5 for inline, h-10 for empty states.
 * 10. Motion: framer-motion with staggered entrance delays (0.1s per item).
 * 11. No max-width on components — let the parent/layout control width.
 * 12. Loading states: always use skeleton placeholders (animate-pulse), never spinners.
 *
 * NOTE: Do NOT run the build command (`npx react-scripts build`).
 */

// ---------------------------------------------------------------------------
// SURFACE CLASSES — cards, panels, containers
// ---------------------------------------------------------------------------

/** Standard card surface (white, bordered, sharp) */
export const card =
  "bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-none";

/** Card with hover lift and shadow */
export const cardInteractive =
  "bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-none hover:shadow-md transition-all duration-200 overflow-hidden";

/** Subtle surface for table rows, inset areas */
export const surfaceMuted =
  "bg-gray-50 dark:bg-gray-900";

/** Page-level background */
export const pageBg =
  "bg-gray-50 dark:bg-gray-900 min-h-screen";

/** Empty state container */
export const emptyState =
  "border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-sm p-8 text-center";

/** Inner divider — card footers, table row borders */
export const dividerInner =
  "border-gray-200 dark:border-gray-700";

// ---------------------------------------------------------------------------
// TYPOGRAPHY CLASSES
// ---------------------------------------------------------------------------

/** Page title — use for dashboard header, main page headings */
export const pageTitle =
  "text-headline font-black tracking-tight text-gray-800 dark:text-white";

/** Section title — use inside SectionHeader */
export const sectionTitle =
  "text-headline font-bold text-gray-900 dark:text-white";

/** Stat number — large accent-colored metric */
export const statNumber =
  "text-3xl sm:text-4xl font-black text-accent1 tracking-tight";

/** Stat number, extra large variant (for hero/featured stats) */
export const statNumberXL =
  "text-4xl font-black tracking-tight text-gray-900 dark:text-white";

/** Card heading — bold, moderate size */
export const cardTitle =
  "text-base font-bold text-gray-900 dark:text-white";

/** Uppercase micro label — used under stats, in legends, table heads */
export const microLabel =
  "text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400";

/** Even wider tracking variant — for stat labels */
export const microLabelWide =
  "text-xs uppercase tracking-widest text-gray-500";

/** Body text — secondary descriptions */
export const bodySecondary =
  "text-xs sm:text-sm text-gray-500 dark:text-gray-400";

/** CTA / action link text */
export const actionText =
  "text-sm font-semibold uppercase tracking-wide text-accent1 hover:text-accent1/80 transition-colors";

// ---------------------------------------------------------------------------
// ACCENT BARS — the editorial signature element
// ---------------------------------------------------------------------------

/** Section-level accent bar (under headings) */
export const accentBarSection =
  "h-1 w-12 bg-accent1 rounded-sm";

/** Card-level accent bar (top of stat cards) */
export const accentBarCard =
  "h-1 w-8 bg-accent1";

// ---------------------------------------------------------------------------
// STATUS BADGE CLASSES
// ---------------------------------------------------------------------------

/** Base badge shape — combine with a status color set below */
export const badgeBase =
  "px-2.5 py-1 rounded-sm text-xs font-medium border";

export const statusStyles = {
  submitted:
    "text-emerald-600 bg-emerald-50 border-emerald-200 dark:text-emerald-400 dark:bg-emerald-900/30 dark:border-emerald-700",
  returned:
    "text-orange-600 bg-orange-50 border-orange-200 dark:text-orange-400 dark:bg-orange-900/30 dark:border-orange-700",
  in_progress:
    "text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-900/30 dark:border-blue-700",
  not_started:
    "text-gray-600 bg-gray-50 border-gray-200 dark:text-gray-300 dark:bg-gray-700/50 dark:border-gray-600",
};

// ---------------------------------------------------------------------------
// ICON SIZING
// ---------------------------------------------------------------------------

export const iconSize = {
  xs: "h-3 w-3",         // inline with text-xs labels
  sm: "h-3.5 w-3.5",     // inline with text-sm body
  md: "h-4 w-4",         // buttons, actions
  lg: "h-5 w-5",         // mobile card icons
  empty: "h-10 w-10",    // empty state illustrations
};

// ---------------------------------------------------------------------------
// MOTION PRESETS — for framer-motion
// ---------------------------------------------------------------------------

/** Fade up entrance for containers */
export const motionFadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4 },
};

/** Staggered child entrance — pass index to get delay */
export const motionStaggerChild = (index, baseDelay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, delay: baseDelay + index * 0.1 },
});

/** Slide in from left */
export const motionSlideLeft = (delay = 0.5) => ({
  initial: { opacity: 0, x: -20 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.5, delay },
});

/** Slide in from right */
export const motionSlideRight = (delay = 0.6) => ({
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.5, delay },
});

/** Hover lift — attach as whileHover */
export const motionHoverLift = { y: -2 };

/** Page/tab transition for AnimatePresence */
export const motionPageTransition = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.3 },
};

// ---------------------------------------------------------------------------
// CHART / DATA-VIZ COLORS — fixed palette for assignment statuses etc.
// ---------------------------------------------------------------------------

export const chartColors = {
  submitted: "#10B981",   // emerald
  pending: "#3B82F6",     // blue
  returned: "#F97316",    // orange
};

// ---------------------------------------------------------------------------
// DARK MODE PAIRS — quick reference for common bg/border combos
// ---------------------------------------------------------------------------
// Use these when you need a one-off and can't use the card/surface presets.
//
//  Light bg       | Dark bg           | Usage
//  ──────────────────────────────────────────────────
//  bg-white       | dark:bg-gray-800  | Cards, panels
//  bg-gray-50     | dark:bg-gray-900  | Muted rows, disabled, page bg
//  border-gray-200| dark:border-gray-700 | Card/panel borders
//  border-gray-200| dark:border-gray-700 | Inner dividers (card footers, table rows)
//  text-gray-900  | dark:text-white   | Primary text
//  text-gray-500  | dark:text-gray-400| Secondary text
//  text-gray-400  | dark:text-gray-500| Tertiary/muted text
